/**
 * Polls `invoice-cancellation-modification/queue-message/:uuid` until Odoo
 * produces a queue.message for the given order, or until a timeout.
 *
 * Used by the Odoo Payment Update page (State B) after triggering invoice
 * generation in the order-service. Odoo creates the queue.message
 * asynchronously, so the page has to wait before it can attach a payment
 * object to it.
 *
 * Spec: docs/superpowers/specs/2026-06-09-odoo-payment-update-design.md
 */

import { ref, onScopeDispose } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';
import { isAttemptNewerThanLogId, type RecoveredRejection } from './odoo-payment-recovery';

export interface QueueMessage {
  /** Null for a body recovered from a rejected request — nothing exists in Odoo yet. */
  id?: number | string | null;
  model?: string;
  format?: boolean;
  operation_type?: string;
  body?: Record<string, unknown>;
  create_date?: string;
}

export interface UseQueueMessagePollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  /**
   * `kw.api.log` id of the rejected attempt already known before triggering, if any. Only a
   * newer attempt counts as the answer to this trigger.
   */
  seenRejectionLogId?: number | null;
}

const DEFAULT_INTERVAL_MS = 5_000;
const DEFAULT_TIMEOUT_MS = 90_000;

export function useQueueMessagePoll() {
  const isPolling = ref(false);
  const elapsedMs = ref(0);
  const queueMessage = ref<QueueMessage | null>(null);
  const error = ref('');
  /**
   * Set when Odoo answered the trigger by refusing the request. Distinguishes "Odoo said no"
   * from "still waiting", which polling for a queue.message alone can never do.
   */
  const rejection = ref<RecoveredRejection | null>(null);

  let intervalHandle: ReturnType<typeof setInterval> | null = null;
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  let startedAt = 0;
  let resolveCurrent: ((value: QueueMessage | null) => void) | null = null;
  let currentUuid = '';
  let currentIntervalMs = DEFAULT_INTERVAL_MS;
  let currentPromise: Promise<QueueMessage | null> | null = null;
  let seenRejectionLogId: number | null = null;

  function clearTimers() {
    if (intervalHandle !== null) {
      clearInterval(intervalHandle);
      intervalHandle = null;
    }
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
  }

  function finish(value: QueueMessage | null) {
    clearTimers();
    isPolling.value = false;
    if (resolveCurrent) {
      resolveCurrent(value);
      resolveCurrent = null;
    }
  }

  async function fetchOnce(): Promise<QueueMessage | null> {
    try {
      const { data } = await apiClient.rpIntegration.get<QueueMessage | null>(
        API_PATHS.rpIntegration.invoiceCancellationModification.queueMessage(currentUuid),
      );
      // The backend returns `null` (200) when no queue.message exists yet.
      if (data && data.id) {
        return data;
      }
      return null;
    } catch (err: any) {
      // Network errors are non-fatal during polling — we retry on the next tick.
      error.value = err?.response?.data?.message ?? err?.message ?? 'Polling failed.';
      return null;
    }
  }

  /**
   * The second signal. Absence of a queue.message has two causes — Odoo has not ingested yet,
   * and Odoo refused the request outright — and only Odoo's inbound HTTP log separates them.
   * Failures here are non-fatal: we simply keep waiting.
   */
  async function fetchRejection(): Promise<RecoveredRejection | null> {
    try {
      const { data } = await apiClient.rpIntegration.get(
        API_PATHS.rpIntegration.odooSaleOrder.rejectedQueueMessageBody(currentUuid),
      );
      const payload = (data?.data ?? null) as RecoveredRejection | null;
      if (!payload?.hasRejectedAttempt) return null;
      return isAttemptNewerThanLogId(payload.sourceLogId, seenRejectionLogId) ? payload : null;
    } catch {
      return null;
    }
  }

  /** Resolves the run once either signal answers; null means timeout, cancel, or rejection. */
  async function checkBothSignals(): Promise<QueueMessage | null> {
    const result = await fetchOnce();
    if (result) return result;
    const rejected = await fetchRejection();
    if (rejected) {
      rejection.value = rejected;
      finish(null);
    }
    return null;
  }

  async function poll() {
    elapsedMs.value = Date.now() - startedAt;
    const result = await checkBothSignals();
    if (result) {
      queueMessage.value = result;
      finish(result);
    }
  }

  async function start(
    uuid: string,
    options: UseQueueMessagePollOptions = {},
  ): Promise<QueueMessage | null> {
    if (isPolling.value) {
      cancel();
    }
    const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    currentUuid = uuid;
    currentIntervalMs = intervalMs;
    seenRejectionLogId = options.seenRejectionLogId ?? null;
    isPolling.value = true;
    elapsedMs.value = 0;
    queueMessage.value = null;
    rejection.value = null;
    error.value = '';
    startedAt = Date.now();

    // First check immediately so we don't burn one interval if Odoo is fast — and Odoo can
    // refuse the request within milliseconds, so check that signal here too.
    const first = await fetchOnce();
    if (first) {
      queueMessage.value = first;
      isPolling.value = false;
      return first;
    }
    const firstRejection = await fetchRejection();
    if (firstRejection) {
      rejection.value = firstRejection;
      isPolling.value = false;
      return null;
    }

    currentPromise = new Promise<QueueMessage | null>((resolve) => {
      resolveCurrent = resolve;
      intervalHandle = setInterval(() => {
        void poll();
      }, intervalMs);
      timeoutHandle = setTimeout(() => {
        // Timeout: leave queueMessage as null, surface the elapsed time.
        elapsedMs.value = Date.now() - startedAt;
        finish(null);
      }, timeoutMs);
    });
    return currentPromise;
  }

  function extend(additionalMs = DEFAULT_TIMEOUT_MS): Promise<QueueMessage | null> {
    if (isPolling.value) {
      // A run is in flight — just push its deadline out.
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
      }
      timeoutHandle = setTimeout(() => {
        elapsedMs.value = Date.now() - startedAt;
        finish(null);
      }, additionalMs);
      return currentPromise ?? Promise.resolve(queueMessage.value);
    }
    // The previous run timed out (finish(null) already resolved it). Resume
    // polling with the accumulated elapsed time so escalation thresholds hold.
    if (!currentUuid) {
      return Promise.resolve(null);
    }
    isPolling.value = true;
    error.value = '';
    currentPromise = new Promise<QueueMessage | null>((resolve) => {
      resolveCurrent = resolve;
      intervalHandle = setInterval(() => {
        void poll();
      }, currentIntervalMs);
      timeoutHandle = setTimeout(() => {
        elapsedMs.value = Date.now() - startedAt;
        finish(null);
      }, additionalMs);
    });
    return currentPromise;
  }

  async function refreshNow(): Promise<QueueMessage | null> {
    if (!currentUuid) return null;
    const result = await checkBothSignals();
    if (result) {
      queueMessage.value = result;
      finish(result);
      return result;
    }
    elapsedMs.value = Date.now() - startedAt;
    return null;
  }

  function cancel() {
    if (!isPolling.value) return;
    finish(null);
  }

  onScopeDispose(() => {
    clearTimers();
    resolveCurrent = null;
  });

  return {
    isPolling,
    elapsedMs,
    queueMessage,
    rejection,
    error,
    start,
    extend,
    refreshNow,
    cancel,
  };
}
